import { Button, makeStyles, Typography, TextField } from "@material-ui/core";
import React, { useState, useEffect } from "react";
import ConfirmationDialog from "../../../components/ConfirmationDialog";
import useToggle from "../../../state/useToggle";
import { useToastError } from "../../../components/FormError";
import api from "../../../api";

export default function PreOptOutUpload({ partnerId, onUploadSuccess }) {
  const classes = useStyles();
  const toggle = useToggle();
  const toastError = useToastError();
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [response, setResponse] = useState("");

  useEffect(() => {
    if (toggle.on) {
      setFile(null);
      setResponse("");
      setIsSubmitting(false);
    }
  }, [toggle.on]);

  const handleFileChange = (event) => {
    const [selectedFile] = event.target.files || [];

    if (!selectedFile || selectedFile.type !== "text/csv") {
      toastError(new Error("Please select a valid CSV file"));
      event.target.value = null;
      return;
    }

    setFile(selectedFile);
    setResponse("");
  };

  const handleSubmit = async () => {
    if (!file) {
      toastError(new Error("Please select a CSV file to upload"));
      return;
    }

    setIsSubmitting(true);
    setResponse("");

    try {
      const formData = new FormData();

      formData.append("csv", file);

      const result = await api.postPreOptOutUpload(partnerId, formData);

      if(onUploadSuccess) onUploadSuccess();
      toggle.turnOff();
      setFile(null);
    } catch (error) {
      toastError(error);
      setResponse(error.message || "An error occurred while processing the file");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={classes.buttonGroup}>
      <Button
        color="secondary"
        variant="contained"
        className={classes.action}
        onClick={toggle.toggle}
      >
        Upload CSV
      </Button>

      <ConfirmationDialog
        open={toggle.on}
        onCancel={toggle.turnOff}
        onConfirm={handleSubmit}
        title="Exclusion list upload"
        cancelProps={{ children: "Close" }}
        confirmProps={{
          children: isSubmitting ? "Submitting..." : "Submit",
          disabled: isSubmitting || !file,
        }}
      >
        <div className={classes.modalContent}>
          <div className={classes.modalInputs}>
            <Typography>
              Upload CSV file
              <div>
              <input
                accept=".csv"
                className={classes.fileInput}
                id="csv-upload"
                type="file"
                onChange={(e) => {
                  handleFileChange(e);
                  e.target.value = "";
                }}
              />
              <label htmlFor="csv-upload">
                <Button variant="outlined" component="span">
                  {file ? file.name : "Upload CSV"}
                </Button>
              </label>
            </div>
            </Typography>
          </div>
        </div>
      </ConfirmationDialog>
    </div>
  );
}

const useStyles = makeStyles((theme) => ({
  action: {
    marginRight: theme.spacing(2),
    boxSizing: "content-box",
  },
  buttonGroup: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  modalContent: {
    padding: theme.spacing(2),
  },
  modalInputs: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  responseText: {
    fontFamily: "monospace",
    backgroundColor: theme.palette.grey[50],
  },
  fileInput: {
    display: "none",
  },
  uploadButton: {
    minWidth: 200,
  },
  downloadLink: {
    color: theme.palette.primary.main,
    textDecoration: "underline",
    padding: 0,
    minWidth: "auto",
    textTransform: "none",
    "&:hover": {
      color: theme.palette.primary.dark,
      backgroundColor: "transparent",
    },
  },
}));
